import { useEffect, useMemo } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import type { OrderListQuery, SubmittedOrder } from '@mypoz/shared';
import {
  clampPageNumber,
  hasActiveOrderListQuery,
  pageCountFor,
  pageRange,
  parseOrderListQuery,
  parsePageNumber,
  parsePageSize,
} from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { EmptyState, Icon, cn } from '@mypoz/ui';

import { Pager } from '@/components/Pager.js';
import { NEW_ORDER_ROUTE } from '@/shell/navigation.js';
import { OrderCard } from '@/features/orders/components/OrderCard.js';
import { OrderFilterBar } from '@/features/orders/components/OrderFilterBar.js';
import { buttonClass } from '@/lib/styles.js';
import { useDebounced } from '@/lib/useDebounced.js';
import { useOrders } from '@/features/orders/context.js';
import { useOrderListPage } from '@/features/orders/useOrderListPage.js';

/**
 * The channels to offer in the filter, taken from the account's orders — every one of which
 * selects something.
 */
function channelOptions(
  orders: readonly SubmittedOrder[],
): readonly { id: string; label: string }[] {
  const seen = new Map<string, string>();

  for (const order of orders) {
    if (order.channelId === undefined || seen.has(order.channelId)) continue;
    seen.set(order.channelId, order.carrier ?? order.channelId);
  }

  return [...seen]
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The Orders list.
 *
 * Filter state lives in the URL rather than in component state, so the back button retraces what
 * you were looking at, a refresh keeps it, and a narrowed list can be sent to someone. Reset is
 * then simply "drop the parameters" (the page size, a preference, is kept).
 *
 * Paged by the server since MYP-126 — see `useOrderListPage`. Each card shows the provider's copy
 * of its order when there is one, so a detail record already opened, or an order just paid or
 * cancelled, shows as it is now rather than as the page first described it.
 */
export function Orders() {
  const { t } = useTranslation();
  const {
    orders: allOrders,
    dictionaryStatus,
    toOrders,
    toOrderFromDetail,
    listVersion,
  } = useOrders();
  const [searchParams, setSearchParams] = useSearchParams();

  const channels = useMemo(() => channelOptions(allOrders), [allOrders]);

  /**
   * `?order=<orderNumber>` (MYP-68) — how a notification jumps straight to one order. With the
   * list paged, that order may not be on page 1, so the link is read as a search for it, and its
   * card opens itself.
   */
  const targetOrderNumber = searchParams.get('order');

  const query = useMemo<OrderListQuery>(() => {
    const parsed = parseOrderListQuery(searchParams);
    return parsed.search === '' && targetOrderNumber
      ? { ...parsed, search: targetOrderNumber }
      : parsed;
  }, [searchParams, targetOrderNumber]);

  const requestedPage = parsePageNumber(searchParams);
  const pageSize = parsePageSize(searchParams);

  // The box updates at once; the vendor is asked once typing pauses.
  const settledSearch = useDebounced(query.search);
  const asked = useMemo(() => ({ ...query, search: settledSearch }), [query, settledSearch]);

  const list = useOrderListPage(asked, requestedPage, pageSize, listVersion, toOrderFromDetail);

  const pageOrders = useMemo<readonly SubmittedOrder[]>(() => {
    const answer = list.answer;
    if (answer === undefined) return [];
    const fromPage =
      answer.kind === 'rows'
        ? toOrders(answer.rows)
        : (() => {
            // An order-number search answers with one order; the chosen chip and channel still apply.
            const order =
              answer.record === undefined ? undefined : toOrderFromDetail(answer.record);
            const matches =
              order !== undefined &&
              (asked.status === 'all' || order.status === asked.status) &&
              (asked.channelId === 'all' || order.channelId === asked.channelId);
            return matches ? [order] : [];
          })();
    const latest = new Map(allOrders.map((order) => [order.orderNumber, order]));
    return fromPage.map((order) => latest.get(order.orderNumber) ?? order);
  }, [list.answer, toOrders, toOrderFromDetail, allOrders, asked.status, asked.channelId]);

  const total =
    list.answer === undefined
      ? 0
      : list.answer.kind === 'rows'
        ? list.answer.total
        : pageOrders.length;
  const pageCount = pageCountFor(total, pageSize);
  const page = clampPageNumber(requestedPage, total, pageSize);

  // A bookmarked or stale `?page=` past the end is replaced by the last page that exists.
  useEffect(() => {
    if (list.status !== 'ready' || list.refreshing || requestedPage === page) return;
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (page <= 1) params.delete('page');
        else params.set('page', String(page));
        return params;
      },
      { replace: true },
    );
  }, [list.status, list.refreshing, requestedPage, page, setSearchParams]);

  /**
   * Writes only what differs from the default, so an unfiltered list has a bare URL. A filter
   * change drops the page and the `?order=` link; the page size is kept.
   *
   * `replace` is passed while typing: without it every keystroke becomes a history entry and the
   * back button walks out of a search one letter at a time.
   */
  const applyQuery = (next: OrderListQuery, options?: { replace?: boolean }) => {
    const params = new URLSearchParams();
    if (next.search.trim()) params.set('q', next.search);
    if (next.status !== 'all') params.set('status', next.status);
    if (next.channelId !== 'all') params.set('channel', next.channelId);
    const size = searchParams.get('size');
    if (size) params.set('size', size);
    setSearchParams(params, { replace: options?.replace ?? false });
  };

  const resetFilters = () => applyQuery({ search: '', status: 'all', channelId: 'all' });

  const goToPage = (next: number) =>
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next <= 1) params.delete('page');
      else params.set('page', String(next));
      return params;
    });

  const changePageSize = (size: number) =>
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('size', String(size));
      params.delete('page');
      return params;
    });

  const failed = list.status === 'error' || dictionaryStatus === 'error';
  const loading = !failed && (list.status === 'loading' || dictionaryStatus === 'loading');
  const counts = list.counts;
  const active = hasActiveOrderListQuery(query);

  /*
    Set by the create and edit flows on their way here, so the confirmation names the order it is
    about. Two separate keys rather than one message with a mode: "submitted" and "saved" are not
    the same event, and a customer who edited a remark should not be told they placed an order.
  */
  const banner = useLocation().state as {
    /**
     * The number `orders_create` gave the new order (MYP-91), or `true` when the vendor said
     * "created" without one — the order exists, it just cannot be named here.
     */
    submitted?: string | true;
    saved?: string;
  } | null;
  const submitted = banner?.submitted;
  const savedNumber = banner?.saved;

  return (
    <>
      <div className="mb-4 flex flex-row items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-ink">{t('orders.list.title')}</h1>
          {/* MYP-128: the subtitle gives way to the list on a phone. */}
          <p className="mt-1 hidden text-sm text-muted sm:block">{t('orders.list.subtitle')}</p>
        </div>

        <Link to={NEW_ORDER_ROUTE} className={buttonClass('primary', 'shrink-0 self-start')}>
          <Icon name="add" size={18} />
          {t('orders.list.newOrder')}
        </Link>
      </div>

      {submitted ? (
        <div className="mb-5 flex items-start gap-3 rounded-lg border border-success/30 bg-success/5 p-4">
          <Icon name="check_circle" size={20} className="shrink-0 text-success" fill />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink">
              {submitted === true
                ? t('orders.list.submittedNoNumber')
                : t('orders.list.submitted', { number: submitted })}
            </p>
            <p className="mt-0.5 text-sm text-muted">{t('orders.list.submittedNote')}</p>
          </div>
        </div>
      ) : null}

      {savedNumber ? (
        <div className="mb-5 flex items-start gap-3 rounded-lg border border-success/30 bg-success/5 p-4">
          <Icon name="check_circle" size={20} className="shrink-0 text-success" fill />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink">
              {t('orders.list.saved', { number: savedNumber })}
            </p>
            <p className="mt-0.5 text-sm text-muted">{t('orders.list.savedNote')}</p>
          </div>
        </div>
      ) : null}

      {!loading && !failed && counts && (counts.all > 0 || active) ? (
        <OrderFilterBar
          query={query}
          counts={counts}
          channels={channels}
          onChange={applyQuery}
          onReset={resetFilters}
        />
      ) : null}

      {/*
        Four states, not two (MYP-53). Loading and failed are about the request; the two
        emptinesses after them are about the answer. "You have no orders" is the wrong thing to
        tell someone whose request never came back, and equally wrong for someone who has four and
        mistyped a search — so each gets its own message and its own action.
      */}
      {failed ? (
        <div className="grid justify-items-center gap-4 py-4">
          <EmptyState
            icon="cloud_off"
            title={t('orders.list.failed')}
            description={t('orders.list.failedBody')}
          />
          <button type="button" onClick={list.retry} className={buttonClass('secondary')}>
            <Icon name="refresh" size={18} />
            {t('orders.list.retry')}
          </button>
        </div>
      ) : loading ? (
        <EmptyState icon="hourglass_empty" title={t('orders.list.loading')} />
      ) : counts?.all === 0 && !active ? (
        <EmptyState
          icon="receipt_long"
          title={t('orders.list.empty')}
          description={t('orders.list.emptyBody')}
        />
      ) : pageOrders.length === 0 ? (
        <div className="grid justify-items-center gap-4 py-4">
          <EmptyState
            icon="search_off"
            title={t('orders.filters.noMatches')}
            description={t('orders.filters.noMatchesBody')}
          />
          <button type="button" onClick={resetFilters} className={buttonClass('secondary')}>
            <Icon name="restart_alt" size={18} />
            {t('orders.filters.reset')}
          </button>
        </div>
      ) : (
        <div
          aria-busy={list.refreshing}
          className={cn('grid gap-4 transition-opacity', list.refreshing && 'opacity-60')}
        >
          {pageOrders.map((order) => (
            <OrderCard
              key={order.orderNumber}
              order={order}
              autoOpen={order.orderNumber === targetOrderNumber}
            />
          ))}
        </div>
      )}

      {!loading && !failed && pageOrders.length > 0 ? (
        <Pager
          label={t('orders.list.pagerLabel')}
          page={page}
          pageCount={pageCount}
          rangeStart={pageRange(page, pageSize, pageOrders.length).start}
          rangeEnd={pageRange(page, pageSize, pageOrders.length).end}
          total={total}
          onGo={goToPage}
          pageSize={pageSize}
          onPageSize={changePageSize}
        />
      ) : null}
    </>
  );
}
